import { useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiGet, apiPatch, operationsClient } from '@/lib/api';
import { settingsEndpoints } from '@/lib/api/endpoints';
import type {
  UpdateEmailSignatureDto,
  UpdateScheduleDisplayTimezoneDto,
  UserDetail,
} from '@/types/settings';

/**
 * Cache key for the current user's profile. Exported so other code
 * paths (e.g. the inbox email composer) that read the signature can
 * subscribe to the same record without spinning up an independent
 * fetch.
 */
export const MY_PROFILE_QUERY_KEY = ['users', 'me'] as const;

/**
 * Fetch the current authenticated user's profile via `GET /users/me`.
 * Does not require any specific permission — any signed-in actor can
 * read their own profile.
 */
export function useMyProfile() {
  return useApiQuery({
    queryKey: MY_PROFILE_QUERY_KEY,
    queryFn: () => apiGet<UserDetail>(operationsClient, settingsEndpoints.userMe()),
    showErrorToast: false,
  });
}

/**
 * Save the current user's email signature. Both fields can be `null`
 * to clear the signature entirely. On success the `users/me` cache is
 * patched in place so the inbox composer's "Include signature"
 * checkbox + content reflect the new value without a refetch.
 */
export function useUpdateMyEmailSignature() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdateEmailSignatureDto) =>
      apiPatch<UserDetail, UpdateEmailSignatureDto>(
        operationsClient,
        settingsEndpoints.userMeEmailSignature(),
        payload,
      ),
    onSuccess: (data) => {
      qc.setQueryData(MY_PROFILE_QUERY_KEY, data);
    },
  });
}

export function useUpdateMyScheduleDisplayTimezone() {
  const qc = useQueryClient();
  return useApiMutation({
    mutationFn: (payload: UpdateScheduleDisplayTimezoneDto) =>
      apiPatch<UserDetail, UpdateScheduleDisplayTimezoneDto>(
        operationsClient,
        settingsEndpoints.userMeScheduleDisplayTimezone(),
        payload,
      ),
    onSuccess: (data) => {
      qc.setQueryData(MY_PROFILE_QUERY_KEY, data);
    },
  });
}
